/**
 * conditional-cascade — EMSPCR Foundation v1.0 §3.
 *
 * Declarative trigger resolver: given a minimal `ComplianceFacts`
 * snapshot of the ePCR state, compute which conditional sets are
 * active right now and which NEMSIS elements they gate.
 *
 * This module is deliberately EPCR-shape-agnostic so both Project C
 * (full platform, apps/web) and Project A (protocol tool,
 * apps/protocol-tool) can consume it from the same package. Each
 * app converts its own record into a `ComplianceFacts` before
 * calling `activeSets()`.
 */

export type ConditionalSetId =
  | 'base'
  | 'trauma'
  | 'trauma_mvc'
  | 'trauma_fall'
  | 'trauma_assault'
  | 'trauma_burn'
  | 'trauma_penetrating'
  | 'trauma_pediatric'
  | 'cardiac_arrest'
  | 'post_rosc'
  | 'termination'
  | 'stroke'
  | 'stemi'
  | 'behavioral'
  | 'pediatric'
  | 'ob'
  | 'refusal'
  | 'doa'
  | 'interfacility'
  | 'non_emergency'
  | 'overdose'
  | 'mass_casualty'
  | 'scheduled_repetitive'
  | 'fire_response'
  | 'serious_unusual'
  | 'crew_injury'
  | 'restraint_use';

// Aligned with RuleStandard from nemsis-elements so callers can pass
// these through the same RuleTag UI without a translation layer.
export type ConditionalRuleStandard =
  | 'NEMSIS' | 'RIDOH' | 'CMS' | 'ACS-2021' | 'Department' | 'FEMA' | 'EMSC' | 'ME/LE'
  | 'NHTSA' | 'NERIS' | 'OSHA' | 'Clinical';

/**
 * Downstream forms/reports that an active set may trigger. The PCR is
 * the source-of-truth event; the downstream form is a separate record
 * that has to be filed because that event happened. Modeling this here
 * keeps the rules in one place — the validator, narrative engine,
 * and Required-Fields Algorithm visualizer all read the same data.
 */
export type TriggeredFormId =
  | 'serious_incident'      // Department serious / unusual occurrence
  | 'neris_fire'            // NERIS fire incident report
  | 'dot_crash'             // DOT (NHTSA) crash data set
  | 'eap_outreach'          // Internal critical-incident peer-support trigger
  | 'mci_ics'               // ICS-201/214 mass-casualty paperwork
  | 'me_referral'           // Medical examiner referral packet
  | 'leo_handoff'           // Law-enforcement handoff documentation
  | 'cms_pcs'               // CMS Physician Certification Statement (non-emergency)
  | 'aba_burn_referral'     // American Burn Association transfer packet
  | 'csi_evidence_log';     // Crime-scene evidence preservation log

export interface TriggeredForm {
  id: TriggeredFormId;
  name: string;
  /** Where this form lives in the app (route or "external"). */
  surface: string;
  /** What standard / regulation requires this form. */
  ruleSources: ReadonlyArray<ConditionalRuleStandard>;
  /** One-line rationale for the algorithm visualizer. */
  rationale: string;
}

export interface ConditionalSet {
  id: ConditionalSetId;
  name: string;
  /** Plain-language explanation shown when user taps the rule tag. */
  rationale: string;
  ruleSources: ReadonlyArray<ConditionalRuleStandard>;
  /** NEMSIS element IDs gated on this set. */
  elements: string[];
  /**
   * Documentation tasks specific to this set that are not standard NEMSIS
   * elements but still gated on it (e.g. LAMS score for stroke,
   * Cincinnati prehospital stroke scale, DOT crash report number).
   * Used by the validator + the Required-Fields visualizer.
   */
  taskElements?: string[];
  /** Forms / reports this set forces downstream. */
  triggeredForms?: TriggeredFormId[];
  parent?: ConditionalSetId;
}

/**
 * Minimal facts each app must hand to the cascade engine. Designed to
 * overlap EPCR fields 1:1 for the web app and be trivially supplied
 * from the protocol tool.
 */
export interface ComplianceFacts {
  patient_dob?: string | null;
  primary_impression?: string | null;
  secondary_impressions?: string[];
  chief_complaint?: string | null;
  dispatch_reason?: string | null;
  possible_injury?: 'yes' | 'no' | 'unknown' | null;
  injury_causes?: string[];
  transport_decision?: string | null;
  hospital_choice_reason?: string | null;
  report_type?: string | null;
  /** Incident type tags from the parent Incident — 'fire' triggers NERIS regardless of EMS findings. */
  incident_types?: string[];
  /** Crew injury flagged on the run (from incident.crew_injury or restraint workflow). */
  crew_injury?: boolean;
  /** Patient was physically or chemically restrained on this run. */
  restraint_used?: boolean;
  /** Patient interaction tags ("Combative", "Weapon present", etc.). */
  interaction_tags?: string[];
}

type Predicate = (facts: ComplianceFacts) => boolean;

/**
 * Registry of downstream forms triggered by active sets. The Required-
 * Fields Algorithm visualizer renders each set's `triggeredForms` as
 * a node; this catalog tells it what each form is and where it lives.
 */
export const TRIGGERED_FORMS: Record<TriggeredFormId, TriggeredForm> = {
  serious_incident: {
    id: 'serious_incident',
    name: 'Serious / Unusual Occurrence Report',
    surface: '/admin/serious-incidents',
    ruleSources: ['Department', 'RIDOH'],
    rationale:
      'Department policy + RIDOH licensure require formal occurrence report when crew injury, weapon contact, restraint, on-duty death, MCI, or pediatric arrest is documented.',
  },
  neris_fire: {
    id: 'neris_fire',
    name: 'NERIS Fire Incident Report',
    surface: '/incidents/:id/fire',
    ruleSources: ['NERIS', 'Department'],
    rationale:
      'Federal NERIS submission required when the unit responded to a fire incident regardless of EMS findings.',
  },
  dot_crash: {
    id: 'dot_crash',
    name: 'DOT Crash Data Set',
    surface: 'epcr:trauma:mvc',
    ruleSources: ['NHTSA', 'NEMSIS'],
    rationale:
      'NHTSA crash surveillance: vehicle damage location, restraint use, airbag deployment, ejection, intrusion, vehicle role, MAIS code.',
  },
  eap_outreach: {
    id: 'eap_outreach',
    name: 'EAP Outreach Trigger',
    surface: '/eap',
    ruleSources: ['Department'],
    rationale:
      'Critical-incident peer support outreach for crew exposed to pediatric death, mass casualty, line-of-duty death, or violent injury.',
  },
  mci_ics: {
    id: 'mci_ics',
    name: 'ICS-201 / 214 Activity Logs',
    surface: 'external',
    ruleSources: ['FEMA', 'Department'],
    rationale:
      'NIMS-required ICS forms when the unit operates within an MCI / multi-agency response.',
  },
  me_referral: {
    id: 'me_referral',
    name: 'Medical Examiner Referral',
    surface: 'external',
    ruleSources: ['ME/LE'],
    rationale:
      'RI medical examiner referral required for unattended death, suspected homicide, suicide, suspicious circumstances, or DOA on scene.',
  },
  leo_handoff: {
    id: 'leo_handoff',
    name: 'Law-Enforcement Handoff Form',
    surface: 'epcr:transfer-of-care',
    ruleSources: ['Department', 'ME/LE'],
    rationale:
      'Documented handoff to LE required when patient is in custody, weapon involved, or evidence preserved.',
  },
  cms_pcs: {
    id: 'cms_pcs',
    name: 'CMS Physician Certification Statement',
    surface: 'epcr:billing',
    ruleSources: ['CMS'],
    rationale:
      'CMS requires a current PCS (≤60 days) on file for any non-emergency transport billed to Medicare.',
  },
  aba_burn_referral: {
    id: 'aba_burn_referral',
    name: 'American Burn Association Transfer Packet',
    surface: 'epcr:trauma:burn',
    ruleSources: ['ACS-2021'],
    rationale:
      'ABA referral criteria met (≥10% TBSA, full-thickness, airway, hand/face/joints, electrical, chemical) — transfer packet must accompany handoff.',
  },
  csi_evidence_log: {
    id: 'csi_evidence_log',
    name: 'Crime-Scene Evidence Log',
    surface: 'external',
    ruleSources: ['ME/LE', 'Department'],
    rationale:
      'Evidence chain-of-custody log required when crew handles, removes, or alters anything on a crime scene (clothing, projectiles, restraints).',
  },
};

const TRAUMA_IMPRESSIONS = [
  'trauma', 'mvc', 'fall', 'assault', 'burn', 'penetrating', 'amputation',
  'laceration', 'blast', 'crush',
];
const STROKE_IMPRESSIONS = ['stroke', 'cva', 'tia'];
const BEHAVIORAL_IMPRESSIONS = ['behavioral', 'psychiatric', 'suicidal', 'agitation'];
const OB_IMPRESSIONS = ['ob', 'obstetric', 'labor', 'pregnancy', 'delivery'];
const OVERDOSE_IMPRESSIONS = ['overdose', 'poisoning', 'intoxication'];

function impressionMatches(facts: ComplianceFacts, terms: string[]): boolean {
  const hay = [
    facts.primary_impression ?? '',
    ...(facts.secondary_impressions ?? []),
    facts.chief_complaint ?? '',
    facts.dispatch_reason ?? '',
  ].join(' ').toLowerCase();
  return terms.some((t) => hay.includes(t));
}

function ageYears(dob: string | null | undefined): number | null {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  return (Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000);
}

export const CONDITIONAL_SETS: Record<ConditionalSetId, ConditionalSet & { test: Predicate }> = {
  base: {
    id: 'base',
    name: 'Base Set',
    rationale: 'Required on every PCR with patient contact. NEMSIS v3.5.1 baseline.',
    ruleSources: ['NEMSIS'],
    elements: [
      'eRecord.01', 'eResponse.05', 'eResponse.13', 'eResponse.14',
      'eDispatch.01', 'eTimes.03', 'eTimes.13',
      'eSituation.11', 'eDisposition.27',
    ],
    test: () => true,
  },
  trauma: {
    id: 'trauma',
    name: 'Trauma Set',
    rationale:
      'NEMSIS v3.5.1 requires the Trauma Set when eSituation.02 Possible Injury = "Yes" or the primary impression falls into trauma. Enables accurate trauma-triage and national trauma-registry reporting.',
    ruleSources: ['NEMSIS', 'ACS-2021'],
    elements: ['eInjury.01', 'eInjury.03', 'eInjury.04', 'eHistory.17'],
    test: (facts) =>
      facts.possible_injury === 'yes' || impressionMatches(facts, TRAUMA_IMPRESSIONS),
  },
  trauma_mvc: {
    id: 'trauma_mvc',
    name: 'MVC Sub-Set (DOT Data Capture)',
    rationale: 'NHTSA crash surveillance + trauma registry. Collected when eInjury.01 includes an MVC code.',
    ruleSources: ['NEMSIS', 'NHTSA', 'Department'],
    elements: ['eInjury.07', 'eInjury.08', 'eInjury.09', 'eInjury.10', 'eInjury.11'],
    taskElements: [
      'crash.vehicle_role',
      'crash.seating_position',
      'crash.restraint_use',
      'crash.airbag_deployment',
      'crash.intrusion_location',
      'crash.intrusion_depth_in',
      'crash.ejection',
      'crash.entrapment',
      'crash.extrication_time_min',
      'crash.estimated_speed_mph',
      'crash.damage_location',
      'crash.death_in_compartment',
    ],
    triggeredForms: ['dot_crash'],
    parent: 'trauma',
    test: (facts) =>
      (facts.injury_causes ?? []).some((c) => /motor vehicle|mvc|auto/i.test(c)),
  },
  trauma_fall: {
    id: 'trauma_fall',
    name: 'Fall Sub-Set',
    rationale: 'Fall height + pattern captured for ACS 2021 yellow criterion (> 10 ft) and registry.',
    ruleSources: ['NEMSIS', 'ACS-2021'],
    elements: [],
    parent: 'trauma',
    test: (facts) => (facts.injury_causes ?? []).some((c) => /fall/i.test(c)),
  },
  trauma_assault: {
    id: 'trauma_assault',
    name: 'Assault Sub-Set',
    rationale: 'Weapon type + LEO involvement captured for registry and scene documentation.',
    ruleSources: ['NEMSIS'],
    elements: [],
    parent: 'trauma',
    test: (facts) => (facts.injury_causes ?? []).some((c) => /assault|firearm|strangulation/i.test(c)),
  },
  trauma_burn: {
    id: 'trauma_burn',
    name: 'Burn Sub-Set',
    rationale: 'Depth + %TBSA + fluid resuscitation for ABA triage.',
    ruleSources: ['NEMSIS', 'ACS-2021'],
    elements: [],
    taskElements: ['burn.tbsa_percent', 'burn.depth', 'burn.mechanism', 'burn.fluid_resuscitation_ml'],
    triggeredForms: ['aba_burn_referral'],
    parent: 'trauma',
    test: (facts) => (facts.injury_causes ?? []).some((c) => /burn/i.test(c)),
  },
  trauma_penetrating: {
    id: 'trauma_penetrating',
    name: 'Penetrating Sub-Set',
    rationale: 'Penetrating injuries trigger red trauma-triage criterion + LEO involvement.',
    ruleSources: ['NEMSIS', 'ACS-2021', 'ME/LE'],
    elements: [],
    taskElements: ['penetrating.weapon_type', 'penetrating.shots_or_wounds_count'],
    triggeredForms: ['leo_handoff', 'csi_evidence_log'],
    parent: 'trauma',
    test: (facts) => (facts.injury_causes ?? []).some((c) => /penetrating|firearm|stab/i.test(c)),
  },
  trauma_pediatric: {
    id: 'trauma_pediatric',
    name: 'Pediatric Trauma Sub-Set',
    rationale: 'ACS 2021 + EMSC: weight-based dosing, Broselow, caregiver + NAT evaluation required.',
    ruleSources: ['ACS-2021', 'EMSC'],
    elements: [],
    taskElements: [
      'peds.weight_kg', 'peds.broselow_color', 'peds.caregiver_present',
      'peds.nat_screen_completed',
    ],
    triggeredForms: ['eap_outreach'],
    parent: 'trauma',
    test: (facts) => {
      const age = ageYears(facts.patient_dob);
      const traumaActive = facts.possible_injury === 'yes' || impressionMatches(facts, TRAUMA_IMPRESSIONS);
      return traumaActive && age !== null && age < 18;
    },
  },
  cardiac_arrest: {
    id: 'cardiac_arrest',
    name: 'Cardiac Arrest Set',
    rationale: 'NEMSIS eArrest set required when eArrest.01 = Yes or Code 99 mode active.',
    ruleSources: ['NEMSIS'],
    elements: [
      'eArrest.01', 'eArrest.02', 'eArrest.03', 'eArrest.04', 'eArrest.07',
      'eArrest.09', 'eArrest.11', 'eArrest.12', 'eArrest.14', 'eArrest.16',
      'eArrest.17', 'eArrest.18', 'eArrest.20', 'eArrest.21', 'eArrest.22',
    ],
    triggeredForms: ['eap_outreach'],
    test: (facts) => impressionMatches(facts, ['cardiac arrest', 'pulseless', 'rosc']),
  },
  post_rosc: {
    id: 'post_rosc',
    name: 'Post-ROSC Sub-Set',
    rationale: 'Post-ROSC care protocol + 12-lead + post-ROSC vital set required after ROSC.',
    ruleSources: ['NEMSIS'],
    elements: [],
    parent: 'cardiac_arrest',
    test: (facts) => /rosc/i.test(facts.primary_impression ?? ''),
  },
  termination: {
    id: 'termination',
    name: 'Termination Sub-Set',
    rationale: 'Medical command contact, physician badge, family notification required when resuscitation is terminated in the field.',
    ruleSources: ['NEMSIS', 'Department', 'ME/LE'],
    elements: [],
    triggeredForms: ['me_referral', 'serious_incident', 'eap_outreach'],
    parent: 'cardiac_arrest',
    test: (facts) => /pronounced|termination/i.test(facts.hospital_choice_reason ?? ''),
  },
  stroke: {
    id: 'stroke',
    name: 'Stroke / CVA Set',
    rationale:
      'Stroke / CVA / TIA: pre-hospital stroke screen (Cincinnati/BEFAST), LAMS severity score, last-known-well time, and pre-arrival stroke-center notification all required for primary stroke center activation.',
    ruleSources: ['NEMSIS', 'ACS-2021'],
    elements: ['eSituation.18', 'eVitals.29', 'eVitals.30'],
    taskElements: [
      'stroke.last_known_well_time',
      'stroke.cincinnati_facial_droop',
      'stroke.cincinnati_arm_drift',
      'stroke.cincinnati_speech',
      'stroke.lams_score',                // 0-5 LAMS severity
      'stroke.lams_facial_droop',
      'stroke.lams_arm_drift',
      'stroke.lams_grip_strength',
      'stroke.bgl_value',                 // hypoglycemia ruled out
      'stroke.center_prenotify_time',
    ],
    test: (facts) => impressionMatches(facts, STROKE_IMPRESSIONS),
  },
  stemi: {
    id: 'stemi',
    name: 'STEMI Set',
    rationale: 'PCI-capable destination + pre-arrival activation when 12-lead interpretation = STEMI.',
    ruleSources: ['NEMSIS'],
    elements: ['eVitals.31', 'eDisposition.24', 'eDisposition.25'],
    taskElements: ['stemi.first_12_lead_time', 'stemi.pci_center_prenotify_time', 'stemi.aspirin_administered'],
    test: (facts) => /stemi/i.test(facts.primary_impression ?? ''),
  },
  behavioral: {
    id: 'behavioral',
    name: 'Behavioral Health Set',
    rationale: 'RI Behavioral Health surveillance + safety / restraint documentation.',
    ruleSources: ['NEMSIS', 'RIDOH'],
    elements: [],
    test: (facts) => impressionMatches(facts, BEHAVIORAL_IMPRESSIONS),
  },
  pediatric: {
    id: 'pediatric',
    name: 'Pediatric Set',
    rationale: 'EMSC pediatric-specific documentation and weight-based dosing.',
    ruleSources: ['NEMSIS', 'EMSC'],
    elements: [],
    test: (facts) => {
      const age = ageYears(facts.patient_dob);
      return age !== null && age < 18;
    },
  },
  ob: {
    id: 'ob',
    name: 'OB Set',
    rationale: 'Obstetric documentation: gestational age, contractions, delivery status, Apgar if applicable.',
    ruleSources: ['NEMSIS'],
    elements: [],
    test: (facts) => impressionMatches(facts, OB_IMPRESSIONS),
  },
  refusal: {
    id: 'refusal',
    name: 'Refusal Set',
    rationale: 'Capacity assessment, risks explained, patient signature, witness documentation per CMS liability standards.',
    ruleSources: ['NEMSIS', 'CMS'],
    elements: ['eDisposition.28', 'eDisposition.30'],
    test: (facts) =>
      facts.transport_decision === 'refusal_care' ||
      facts.transport_decision === 'refusal_transport',
  },
  doa: {
    id: 'doa',
    name: 'DOA Set',
    rationale: 'Pronouncement requires ME/LE policy documentation, time of pronouncement, disposition chain.',
    ruleSources: ['NEMSIS', 'ME/LE'],
    elements: [],
    triggeredForms: ['me_referral'],
    test: (facts) => /pronouncement|doa|dead on arrival/i.test(facts.hospital_choice_reason ?? ''),
  },
  interfacility: {
    id: 'interfacility',
    name: 'Interfacility Set',
    rationale: 'CMS billing: reason for interfacility transfer, sending facility details, accepting physician.',
    ruleSources: ['NEMSIS', 'CMS'],
    elements: ['eSituation.20'],
    triggeredForms: ['cms_pcs'],
    test: (facts) => facts.report_type === 'interfacility',
  },
  non_emergency: {
    id: 'non_emergency',
    name: 'Non-Emergency Set',
    rationale: 'CMS: non-emergency transport requires a valid Physician Certification Statement (CMN) within 60 days.',
    ruleSources: ['CMS'],
    elements: [],
    triggeredForms: ['cms_pcs'],
    test: (facts) =>
      facts.report_type === 'non_transport' ||
      /non.?emergency/i.test(facts.hospital_choice_reason ?? ''),
  },
  overdose: {
    id: 'overdose',
    name: 'Overdose Set',
    rationale: 'RIDOH opioid overdose surveillance — Narcan given OR primary impression = overdose flags the case for CSTE dataset.',
    ruleSources: ['NEMSIS', 'RIDOH'],
    elements: ['eHistory.17'],
    test: (facts) => impressionMatches(facts, OVERDOSE_IMPRESSIONS),
  },
  mass_casualty: {
    id: 'mass_casualty',
    name: 'Mass Casualty Set',
    rationale: 'FEMA + NEMSIS MCI documentation: triage classification, on-scene IC, resource request timeline.',
    ruleSources: ['NEMSIS', 'FEMA'],
    elements: ['eScene.07', 'eScene.08'],
    triggeredForms: ['mci_ics', 'serious_incident', 'eap_outreach'],
    test: () => false,
  },
  scheduled_repetitive: {
    id: 'scheduled_repetitive',
    name: 'Scheduled Repetitive Set',
    rationale: 'CMS non-emergency + recurring schedule (dialysis): annual standing CMN + 60-day re-verification.',
    ruleSources: ['CMS'],
    elements: [],
    triggeredForms: ['cms_pcs'],
    test: () => false,
  },
  fire_response: {
    id: 'fire_response',
    name: 'Fire Response Set',
    rationale:
      'Unit responded to a fire incident — federal NERIS submission required regardless of EMS findings. Always paired with an EMS PCR if patients were assessed.',
    ruleSources: ['NERIS', 'Department'],
    elements: [],
    taskElements: [
      'neris.incident_type', 'neris.actions_taken', 'neris.cause', 'neris.water_supply',
    ],
    triggeredForms: ['neris_fire'],
    test: (facts) => (facts.incident_types ?? []).some((t) => /fire/i.test(t)),
  },
  serious_unusual: {
    id: 'serious_unusual',
    name: 'Serious / Unusual Occurrence Set',
    rationale:
      'Department + RIDOH require a Serious / Unusual Occurrence Report for: pediatric arrest, line-of-duty death, weapon contact, MCI, on-duty injury, restraint, or DOA.',
    ruleSources: ['Department', 'RIDOH'],
    elements: [],
    taskElements: [
      'serious.incident_category',
      'serious.brief_summary',
      'serious.notifying_officer',
      'serious.medical_director_notified_at',
    ],
    triggeredForms: ['serious_incident', 'eap_outreach'],
    test: (facts) => {
      const age = ageYears(facts.patient_dob);
      const pedsArrest = age !== null && age < 18 &&
        impressionMatches(facts, ['cardiac arrest', 'pulseless']);
      const weaponInvolved = (facts.injury_causes ?? [])
        .some((c) => /firearm|stab|penetrating|weapon/i.test(c));
      const isDOA = /pronouncement|doa|dead on arrival/i
        .test(facts.hospital_choice_reason ?? '');
      const isRestraint = facts.restraint_used === true;
      const isCrewInjury = facts.crew_injury === true;
      return pedsArrest || weaponInvolved || isDOA || isRestraint || isCrewInjury;
    },
  },
  crew_injury: {
    id: 'crew_injury',
    name: 'Crew Injury Set',
    rationale:
      'On-duty crew injury triggers OSHA 300/301 reporting + workers-comp documentation + EAP contact.',
    ruleSources: ['OSHA', 'Department'],
    elements: [],
    taskElements: [
      'crew_injury.injured_member', 'crew_injury.mechanism', 'crew_injury.body_part',
      'crew_injury.treated_at', 'crew_injury.lost_time',
    ],
    triggeredForms: ['serious_incident', 'eap_outreach'],
    test: (facts) => facts.crew_injury === true,
  },
  restraint_use: {
    id: 'restraint_use',
    name: 'Restraint Set',
    rationale:
      'Physical or chemical restraint requires capacity + medical-necessity documentation, monitoring schedule (q5min), and command physician contact.',
    ruleSources: ['Department', 'NEMSIS', 'RIDOH'],
    elements: [],
    taskElements: [
      'restraint.type', 'restraint.justification', 'restraint.physician_contact_time',
      'restraint.monitoring_schedule', 'restraint.removal_time',
    ],
    triggeredForms: ['serious_incident', 'leo_handoff'],
    test: (facts) => facts.restraint_used === true,
  },
};

export function activeSets(facts: ComplianceFacts): ConditionalSet[] {
  const out: ConditionalSet[] = [];
  for (const set of Object.values(CONDITIONAL_SETS)) {
    if (set.test(facts)) {
      const { test: _test, ...pub } = set;
      void _test;
      out.push(pub);
    }
  }
  return out.sort((a, b) => {
    if (a.id === 'base') return -1;
    if (b.id === 'base') return 1;
    const ap = a.parent ? 1 : 0;
    const bp = b.parent ? 1 : 0;
    return ap - bp;
  });
}

export function isSetActive(facts: ComplianceFacts, id: ConditionalSetId): boolean {
  return CONDITIONAL_SETS[id]?.test(facts) ?? false;
}

export function conditionallyRequiredElements(facts: ComplianceFacts): string[] {
  const out = new Set<string>();
  for (const set of activeSets(facts)) {
    for (const el of set.elements) out.add(el);
  }
  return Array.from(out);
}

/**
 * Conditionally-required *task* elements (LAMS sub-fields, DOT crash
 * fields, etc.). These are EMSPCR-specific documentation tasks gated
 * by an active set; they're separate from the NEMSIS element catalog
 * because NEMSIS doesn't standardize all of them.
 */
export function conditionallyRequiredTasks(facts: ComplianceFacts): string[] {
  const out = new Set<string>();
  for (const set of activeSets(facts)) {
    for (const t of set.taskElements ?? []) out.add(t);
  }
  return Array.from(out);
}

/**
 * Resolve the downstream forms that are triggered by the currently-
 * active sets. Used by the Required-Fields visualizer + the dashboard
 * "what else do I have to file?" indicator.
 */
export function triggeredForms(facts: ComplianceFacts): TriggeredForm[] {
  const seen = new Set<TriggeredFormId>();
  const out: TriggeredForm[] = [];
  for (const set of activeSets(facts)) {
    for (const id of set.triggeredForms ?? []) {
      if (seen.has(id)) continue;
      seen.add(id);
      const form = TRIGGERED_FORMS[id];
      if (form) out.push(form);
    }
  }
  return out;
}

/**
 * Reverse lookup: which sets, if active, would trigger a given form?
 * Used by the visualizer to draw upstream edges into a form node.
 */
export function setsThatTriggerForm(formId: TriggeredFormId): ConditionalSetId[] {
  const out: ConditionalSetId[] = [];
  for (const set of Object.values(CONDITIONAL_SETS)) {
    if ((set.triggeredForms ?? []).includes(formId)) {
      out.push(set.id);
    }
  }
  return out;
}
